// src/Pages/Hero.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Activity, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import video from '../assets/MainHero.mp4';

const Hero: React.FC = () => {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const liveTrades = [
    { symbol: "NIFTY50", value: "22,453.30", change: "+124.50", isProfit: true, percentage: "+0.55%" },
    { symbol: "BANKNIFTY", value: "47,890.15", change: "-210.40", isProfit: false, percentage: "-0.43%" },
  ];

  return (
    <section className="relative w-full h-auto md:h-[650px] min-h-[650px] mt-[80px] lg:mt-[90px] overflow-hidden bg-[#0b1221] text-white flex items-center font-[Urbanist]">
      
      {/* BACKGROUND LAYER */}
      <div className="absolute inset-0 z-0 overflow-hidden bg-[#0b1221]">
        <video 
          src={video}
          autoPlay 
          loop 
          muted 
          playsInline
          preload="auto"
          /* Desktop par brightness 100% rakhi hai taaki left side ekdum clear rahe, Mobile par text readability ke liye thodi dark hai */
          className="w-full h-full object-cover object-center brightness-[0.5] md:brightness-100 transition-all duration-1000"
        />
        
        {/* Mobile Gradient: Bottom to Top */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1221] via-[#0b1221]/80 to-transparent md:hidden"></div>
        
        {/* Desktop Gradient: Sirf right side par anchored hai, left side ekdum khali aur clear chhod di hai */}
        <div className="absolute top-0 right-0 bottom-0 w-full md:w-[85%] lg:w-[70%] bg-gradient-to-l from-[#0b1221] via-[#0b1221]/70 to-transparent hidden md:block"></div>
      </div>

      {/* CONTENT CONTAINER */}
      <div className="relative z-10 w-full px-4 md:px-12 lg:px-24 max-w-[1600px] mx-auto py-12 md:py-0">
        
        <div className="w-full flex flex-col gap-12 lg:gap-20">
          
          {/* MAIN CONTENT AREA (Right Aligned on Desktop) */}
          <div className="w-full md:w-[90%] lg:w-3/4 xl:w-2/3 flex flex-col items-center md:items-end text-center md:text-right md:ml-auto">
            
            {/* TAGLINE BADGE */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-amber-500/30 backdrop-blur-md"
            >
              <ShieldCheck size={16} className="text-amber-400" />
              <span className="text-sm md:text-base font-semibold text-amber-100 tracking-wide">
                Consistency Over Emotions.
              </span>
            </motion.div>

            {/* HEADINGS */}
            <motion.div 
              initial={{ opacity: 0, ...(isDesktop ? { x: 40, y: 0 } : { x: 0, y: 20 }) }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-2 md:space-y-4 w-full"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-[1.05] text-white drop-shadow-[0_5px_25px_rgba(0,0,0,0.8)]">
                Where Strategy
              </h1>
              <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold tracking-tight leading-[1.05] text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-yellow-200 to-white drop-shadow-[0_5px_25px_rgba(0,0,0,0.8)]">
                 Meets Automation
              </h1>
            </motion.div>

            {/* SUBTITLE */}
            <motion.p 
              initial={{ opacity: 0, ...(isDesktop ? { x: 40, y: 0 } : { x: 0, y: 20 }) }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mt-6 text-base md:text-lg lg:text-xl text-gray-300 max-w-xl font-medium tracking-wide drop-shadow-lg"
            >
              <span className="text-white font-semibold">Trade Smart. Trade Automated.</span> <br className="hidden md:block" />
              The best trades require algorithmic precision, research, and zero hesitation.
            </motion.p>

            {/* WIDGET & BUTTON ROW */}
            <div className="mt-8 flex flex-col md:flex-row items-center justify-end gap-6 md:gap-8 w-full">
              
              {/* WIDGET AREA (Left of the button on Desktop) */}
              <motion.div 
                initial={{ opacity: 0, ...(isDesktop ? { x: -20, y: 0 } : { x: 0, y: 20 }) }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="w-full sm:w-auto bg-[#0b1221]/70 backdrop-blur-xl border border-amber-500/10 p-5 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] min-w-[280px] md:min-w-[300px] text-left"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shadow-[0_0_10px_rgba(251,191,36,0.8)]"></div>
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                    <Activity size={14} className="text-amber-500" /> Live Market Algo
                  </span>
                </div>
                
                <div className="space-y-4">
                  {liveTrades.map((trade, idx) => (
                    <div key={idx} className="flex justify-between items-center border-b border-white/5 pb-3 last:border-0 last:pb-0">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-200">{trade.symbol}</span>
                        <span className="text-sm text-gray-500">{trade.value}</span>
                      </div>
                      <div className={`flex flex-col items-end font-semibold ${trade.isProfit ? 'text-green-500' : 'text-red-500'}`}>
                        <span className="flex items-center gap-1 text-base">
                          {trade.isProfit ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                          {trade.change}
                        </span>
                        <span className="text-xs opacity-80 bg-white/5 px-2 py-0.5 rounded-md mt-1 font-medium">
                          {trade.percentage}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* BUTTON AREA (Right Side) */}
              <motion.div 
                initial={{ opacity: 0, ...(isDesktop ? { x: 40, y: 0 } : { x: 0, y: 20 }) }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="flex flex-col items-center md:items-end gap-4 w-full md:w-auto"
              >
                <Link to="/demat/register" className="w-full md:w-auto">
                  <motion.button 
                    whileHover={{ scale: 1.03, boxShadow: "0 0 30px rgba(245,158,11,0.4)" }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full md:w-auto bg-gradient-to-r from-red-600 to-red-500 text-white px-8 py-4 rounded-2xl md:rounded-full text-lg font-bold hover:from-amber-500 hover:to-amber-400 transition-all shadow-[0_10px_30px_rgba(245,158,11,0.25)] border border-amber-300/30 flex items-center justify-center gap-3 whitespace-nowrap"
                  >
                    Get started for free
                    <Activity size={20} className="hidden md:block" />
                  </motion.button>
                </Link>
                
                <span className="text-sm text-gray-400 font-medium bg-[#0b1221]/50 px-4 py-1.5 rounded-full backdrop-blur-sm border border-white/5 whitespace-nowrap">
                  $0 forever, no credit card needed
                </span>
              </motion.div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;